import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { X, Send, Bug, Lightbulb, HelpCircle, ThumbsUp, AlertCircle, CheckCircle2 } from 'lucide-react';
import Button from './ui/Button';

const FeedbackModal = ({ user, onClose }) => {
  const [type, setType] = useState('bug'); // bug, feature, question, praise
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('medium');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const feedbackTypes = [
    { id: 'bug', label: '🐛 Bug', desc: 'Signaler un problème', icon: Bug, color: 'red' },
    { id: 'feature', label: '💡 Amélioration', desc: 'Proposer une nouvelle fonctionnalité', icon: Lightbulb, color: 'blue' },
    { id: 'question', label: '❓ Question', desc: 'Besoin d\'aide ou d\'information', icon: HelpCircle, color: 'amber' },
    { id: 'praise', label: '👍 Compliment', desc: 'Ce qui fonctionne bien', icon: ThumbsUp, color: 'green' }
  ];

  const priorities = [
    { id: 'low', label: 'Basse', color: 'text-slate-600' },
    { id: 'medium', label: 'Moyenne', color: 'text-blue-600' },
    { id: 'high', label: 'Haute', color: 'text-orange-600' },
    { id: 'critical', label: 'Critique', color: 'text-red-600' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSending(true);
    try {
      await addDoc(collection(db, 'feedback'), {
        userId: user.uid,
        userEmail: user.email,
        type,
        subject: subject.trim() || 'Sans titre',
        message: message.trim(),
        priority,
        status: 'new',
        createdAt: serverTimestamp(),
        userAgent: navigator.userAgent,
        url: window.location.href
      });

      setSent(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Erreur envoi feedback:', error);
      alert('Erreur lors de l\'envoi. Veuillez réessayer.');
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-in zoom-in">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={32} className="text-green-600"/>
          </div>
          <h3 className="text-2xl font-bold text-slate-800 mb-2">Merci ! 🎉</h3>
          <p className="text-slate-600">
            Votre retour a été envoyé avec succès. Nous l'examinerons rapidement.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in slide-in-from-bottom-4" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold mb-1">💬 Votre avis compte !</h2>
              <p className="text-blue-100 text-sm">Aidez-nous à améliorer BatiFlow</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition">
              <X size={24}/>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          
          {/* Type de feedback */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-3">Type de retour</label>
            <div className="grid grid-cols-2 gap-3">
              {feedbackTypes.map(t => {
                const Icon = t.icon;
                const isSelected = type === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setType(t.id)}
                    className={`p-4 rounded-xl border-2 transition text-left ${
                      isSelected 
                        ? `border-${t.color}-500 bg-${t.color}-50` 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={20} className={isSelected ? `text-${t.color}-600` : 'text-slate-400'}/>
                      <span className="font-bold text-slate-800">{t.label}</span>
                    </div>
                    <p className="text-xs text-slate-500">{t.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Priorité (seulement pour bugs) */}
          {type === 'bug' && (
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-3">Priorité</label>
              <div className="flex gap-2">
                {priorities.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPriority(p.id)}
                    className={`flex-1 px-4 py-2 rounded-lg border-2 font-medium transition ${
                      priority === p.id 
                        ? 'border-slate-800 bg-slate-800 text-white' 
                        : 'border-slate-200 hover:border-slate-300 text-slate-600'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sujet */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              Sujet {type === 'bug' && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={
                type === 'bug' ? "Ex: La signature ne s'affiche pas" :
                type === 'feature' ? "Ex: Ajouter export PDF personnalisé" :
                type === 'question' ? "Ex: Comment inviter un collaborateur ?" :
                "Ex: Interface très intuitive"
              }
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-blue-500 transition"
              required={type === 'bug'}
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                type === 'bug' ? "Décrivez le problème : Que s'est-il passé ? Quand ? Sur quelle page ?" :
                type === 'feature' ? "Décrivez la fonctionnalité souhaitée et comment elle vous aiderait..." :
                type === 'question' ? "Posez votre question en détail..." :
                "Qu'est-ce qui vous a plu ? Qu'est-ce qui fonctionne bien ?"
              }
              rows={6}
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-blue-500 transition resize-none"
              required
            />
            <p className="text-xs text-slate-500 mt-2">
              Plus vous êtes précis, mieux nous pourrons vous aider !
            </p>
          </div>

          {/* Info contextuelle */}
          {type === 'bug' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex gap-3">
                <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20}/>
                <div className="text-sm text-amber-800">
                  <strong>Astuce :</strong> Pour nous aider à corriger rapidement, précisez :
                  <ul className="list-disc list-inside mt-2 space-y-1 text-amber-700">
                    <li>Les étapes pour reproduire le bug</li>
                    <li>Ce que vous attendiez comme résultat</li>
                    <li>Sur quel appareil (ordinateur, tablette, téléphone)</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Boutons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition"
              disabled={sending}
            >
              Annuler
            </button>
            <Button
              type="submit"
              disabled={sending || !message.trim()}
              className="flex-1"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Envoi...
                </>
              ) : (
                <>
                  <Send size={18} className="mr-2"/>
                  Envoyer
                </>
              )}
            </Button>
          </div>

        </form>

      </div>
    </div>
  );
};

export default FeedbackModal;
